import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Box,
  Tooltip
} from "@material-ui/core";
import {
  Add as AddIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Visibility as VisibilityIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  GetApp as DownloadIcon,
  Assessment as ReportIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  campaignCard: {
    marginBottom: theme.spacing(2),
    cursor: "pointer",
    transition: "all 0.3s",
    "&:hover": {
      boxShadow: theme.shadows[4],
    },
  },
  statusChip: {
    fontWeight: "bold",
  },
  progressBar: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  uploadButton: {
    marginTop: theme.spacing(2),
  },
}));

const VapiCalls = () => {
  const classes = useStyles();

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignReport, setCampaignReport] = useState(null);

  // Form state
  const [campaignName, setCampaignName] = useState("");
  const [assistantId, setAssistantId] = useState("");
  const [contactFile, setContactFile] = useState(null);
  const [phoneNumbers, setPhoneNumbers] = useState("");
  const [phoneCount, setPhoneCount] = useState(0);
  const [phoneWarnings, setPhoneWarnings] = useState([]);
  const [phoneErrors, setPhoneErrors] = useState([]);
  const [inputMethod, setInputMethod] = useState("textarea"); // "textarea" or "file"

  useEffect(() => {
    loadCampaigns();
    // Refresh every 5 seconds
    const interval = setInterval(loadCampaigns, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadCampaigns = async () => {
    try {
      const { data } = await api.get("/api/vapi/campaigns");
      setCampaigns(data);
    } catch (error) {
      console.error("Error loading campaigns:", error);
    }
  };

  const handleCreateCampaign = async () => {
    try {
      if (!campaignName) {
        toast.error("Por favor ingresa el nombre de la campaña");
        return;
      }

      if (inputMethod === "textarea" && !phoneNumbers.trim()) {
        toast.error("Por favor ingresa al menos un número telefónico");
        return;
      }

      if (inputMethod === "file" && !contactFile) {
        toast.error("Por favor selecciona un archivo de contactos");
        return;
      }

      setLoading(true);

      const formData = new FormData();
      formData.append("name", campaignName);
      formData.append("assistantId", assistantId);

      if (inputMethod === "textarea") {
        formData.append("phoneNumbers", phoneNumbers);
      } else {
        formData.append("contacts", contactFile);
      }

      const { data } = await api.post("/api/vapi/campaigns", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Show warnings if any
      if (data.warnings && data.warnings.length > 0) {
        data.warnings.forEach(warning => toast.warning(warning));
      }

      toast.success("Campaña creada exitosamente");
      setCreateDialogOpen(false);
      resetForm();
      loadCampaigns();
    } catch (error) {
      console.error("Error creating campaign:", error);
      toast.error(error.response?.data?.error || "Error al crear la campaña");
    } finally {
      setLoading(false);
    }
  };

  const handleStartCampaign = async (campaignId) => {
    try {
      await api.post(`/api/vapi/campaigns/${campaignId}/start`);
      toast.success("Campaña iniciada");
      loadCampaigns();
    } catch (error) {
      console.error("Error starting campaign:", error);
      toast.error("Error al iniciar la campaña");
    }
  };

  const handlePauseCampaign = async (campaignId) => {
    try {
      await api.post(`/api/vapi/campaigns/${campaignId}/pause`);
      toast.success("Campaña pausada");
      loadCampaigns();
    } catch (error) {
      console.error("Error pausing campaign:", error);
      toast.error("Error al pausar la campaña");
    }
  };

  const handleStopCampaign = async (campaignId) => {
    try {
      await api.post(`/api/vapi/campaigns/${campaignId}/stop`);
      toast.success("Campaña detenida");
      loadCampaigns();
    } catch (error) {
      console.error("Error stopping campaign:", error);
      toast.error("Error al detener la campaña");
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta campaña?")) {
      return;
    }

    try {
      await api.delete(`/api/vapi/campaigns/${campaignId}`);
      toast.success("Campaña eliminada");
      loadCampaigns();
    } catch (error) {
      console.error("Error deleting campaign:", error);
      toast.error("Error al eliminar la campaña");
    }
  };

  const handleViewDetails = async (campaign) => {
    try {
      const { data } = await api.get(`/api/vapi/campaigns/${campaign.id}`);
      setSelectedCampaign(data);
      setDetailsDialogOpen(true);
    } catch (error) {
      console.error("Error loading campaign details:", error);
      toast.error("Error al cargar los detalles");
    }
  };

  const handleViewReport = async (campaign) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/api/vapi/campaigns/${campaign.id}/report`);
      setCampaignReport(data);
      setReportDialogOpen(true);
    } catch (error) {
      console.error("Error loading campaign report:", error);
      toast.error("Error al cargar el reporte");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadRecording = async (contactId) => {
    try {
      const response = await api.get(`/api/vapi/contacts/${contactId}/recording`, {
        responseType: "blob"
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `grabacion-${contactId}.mp3`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Grabación descargada");
    } catch (error) {
      console.error("Error downloading recording:", error);
      toast.error("Error al descargar la grabación");
    }
  };

  const resetForm = () => {
    setCampaignName("");
    setAssistantId("");
    setContactFile(null);
    setPhoneNumbers("");
    setPhoneCount(0);
    setPhoneWarnings([]);
    setPhoneErrors([]);
    setInputMethod("textarea");
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "default",
      running: "primary",
      paused: "secondary",
      completed: "default",
      cancelled: "default",
    };
    return colors[status] || "default";
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: "Borrador",
      running: "En Ejecución",
      paused: "Pausada",
      completed: "Completada",
      cancelled: "Cancelada",
    };
    return labels[status] || status;
  };

  const calculateProgress = (campaign) => {
    if (!campaign.totalContacts) return 0;
    const completed = campaign.completedCalls + campaign.failedCalls;
    return (completed / campaign.totalContacts) * 100;
  };

  const handlePhoneNumbersChange = (e) => {
    const text = e.target.value;
    setPhoneNumbers(text);

    // Count numbers (split by lines or commas)
    const lines = text
      .split(/[\n,;]/)
      .map(l => l.trim())
      .filter(l => l.length > 0);
    
    const errors = [];
    const warnings = [];
    let validCount = 0;

    lines.forEach((line, index) => {
      // Verificar que contenga al menos dígitos
      if (!/\d/.test(line)) {
        errors.push(`Línea ${index + 1}: No contiene dígitos`);
      } else {
        validCount++;
        
        // Advertir si no tiene código de país
        if (!/^\+/.test(line)) {
          warnings.push(`Línea ${index + 1}: Se asumirá código de país +1`);
        }
      }
    });

    setPhoneCount(validCount);
    setPhoneErrors(errors);
    setPhoneWarnings(warnings.slice(0, 3)); // Mostrar solo las primeras 3 advertencias
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Llamadas Masivas con Vapi.ai</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setCreateDialogOpen(true)}
            startIcon={<AddIcon />}
          >
            Nueva Campaña
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container spacing={2}>
          {campaigns.length === 0 ? (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="body1" align="center" color="textSecondary">
                    No hay campañas creadas. Crea tu primera campaña para comenzar.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            campaigns.map((campaign) => (
              <Grid item xs={12} key={campaign.id}>
                <Card className={classes.campaignCard}>
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <Typography variant="h6">{campaign.name}</Typography>
                        <Chip
                          label={getStatusLabel(campaign.status)}
                          color={getStatusColor(campaign.status)}
                          size="small"
                          className={classes.statusChip}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="textSecondary">
                          Total: {campaign.totalContacts} contactos
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Completadas: {campaign.completedCalls} | Fallidas: {campaign.failedCalls}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={calculateProgress(campaign)}
                          className={classes.progressBar}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box display="flex" justifyContent="flex-end" gap={1}>
                          {campaign.status === "draft" && (
                            <Tooltip title="Iniciar">
                              <IconButton
                                color="primary"
                                onClick={() => handleStartCampaign(campaign.id)}
                              >
                                <PlayIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {campaign.status === "running" && (
                            <Tooltip title="Pausar">
                              <IconButton
                                color="secondary"
                                onClick={() => handlePauseCampaign(campaign.id)}
                              >
                                <PauseIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {(campaign.status === "running" || campaign.status === "paused") && (
                            <Tooltip title="Detener">
                              <IconButton
                                onClick={() => handleStopCampaign(campaign.id)}
                              >
                                <StopIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Ver Detalles">
                            <IconButton
                              onClick={() => handleViewDetails(campaign)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ver Reporte">
                            <IconButton
                              color="primary"
                              onClick={() => handleViewReport(campaign)}
                            >
                              <ReportIcon />
                            </IconButton>
                          </Tooltip>
                          {campaign.status === "draft" && (
                            <Tooltip title="Eliminar">
                              <IconButton
                                onClick={() => handleDeleteCampaign(campaign.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      </Paper>

      {/* Create Campaign Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Nueva Campaña de Llamadas</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            variant="outlined"
            label="Nombre de la Campaña"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            variant="outlined"
            label="Assistant ID (Opcional)"
            value={assistantId}
            onChange={(e) => setAssistantId(e.target.value)}
            margin="normal"
            helperText="Deja vacío para usar el asistente predeterminado"
          />

          <Box mt={3} mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              Números Telefónicos
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={8}
              variant="outlined"
              placeholder={`Pega los números telefónicos aquí (uno por línea o separados por comas)

Ejemplos de formatos aceptados:
+1234567890
+1 (555) 123-4567
5551234567
+52 55 1234 5678`}
              value={phoneNumbers}
              onChange={handlePhoneNumbersChange}
              error={phoneErrors.length > 0}
              helperText={
                phoneErrors.length > 0
                  ? `${phoneErrors.length} error(es) encontrado(s)`
                  : phoneCount > 0
                  ? `${phoneCount} número(s) válido(s) detectado(s)`
                  : "Ingresa los números telefónicos"
              }
            />
            
            {/* Mostrar errores */}
            {phoneErrors.length > 0 && (
              <Box mt={1} p={1} bgcolor="error.light" borderRadius={1}>
                <Typography variant="caption" color="error" display="block">
                  <strong>Errores:</strong>
                </Typography>
                {phoneErrors.slice(0, 3).map((error, index) => (
                  <Typography key={index} variant="caption" color="error" display="block">
                    • {error}
                  </Typography>
                ))}
                {phoneErrors.length > 3 && (
                  <Typography variant="caption" color="error" display="block">
                    ... y {phoneErrors.length - 3} error(es) más
                  </Typography>
                )}
              </Box>
            )}

            {/* Mostrar warnings */}
            {phoneWarnings.length > 0 && phoneErrors.length === 0 && (
              <Box mt={1} p={1} bgcolor="warning.light" borderRadius={1}>
                <Typography variant="caption" style={{ color: '#856404' }} display="block">
                  <strong>Advertencias:</strong>
                </Typography>
                {phoneWarnings.map((warning, index) => (
                  <Typography key={index} variant="caption" style={{ color: '#856404' }} display="block">
                    • {warning}
                  </Typography>
                ))}
              </Box>
            )}

            <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
              Los números sin código de país se asumirán como +1 (USA). Se eliminarán automáticamente los duplicados.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleCreateCampaign}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            Crear Campaña
          </Button>
        </DialogActions>
      </Dialog>

      {/* Campaign Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Detalles de la Campaña</DialogTitle>
        <DialogContent>
          {selectedCampaign && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6">{selectedCampaign.name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  Estado: {getStatusLabel(selectedCampaign.status)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1">Estadísticas</Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Total de Contactos</TableCell>
                      <TableCell align="right">{selectedCampaign.totalContacts}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Llamadas Completadas</TableCell>
                      <TableCell align="right">{selectedCampaign.completedCalls}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Llamadas Fallidas</TableCell>
                      <TableCell align="right">{selectedCampaign.failedCalls}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Pendientes</TableCell>
                      <TableCell align="right">{selectedCampaign.pendingCalls}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>En Progreso</TableCell>
                      <TableCell align="right">{selectedCampaign.inProgressCalls}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Campaign Report Dialog */}
      <Dialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Reporte de Campaña</DialogTitle>
        <DialogContent>
          {campaignReport && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6">{campaignReport.campaign.name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  Estado: {getStatusLabel(campaignReport.campaign.status)}
                </Typography>
              </Grid>

              {/* Summary Statistics */}
              <Grid item xs={12}>
                <Paper style={{ padding: 16, backgroundColor: "#f5f5f5" }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">
                        Total
                      </Typography>
                      <Typography variant="h5">
                        {campaignReport.summary.total}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">
                        Contestaron
                      </Typography>
                      <Typography variant="h5" style={{ color: "#4caf50" }}>
                        {campaignReport.summary.answered}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">
                        No Contestaron
                      </Typography>
                      <Typography variant="h5" style={{ color: "#f44336" }}>
                        {campaignReport.summary.notAnswered}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">
                        Tasa de Respuesta
                      </Typography>
                      <Typography variant="h5">
                        {campaignReport.summary.answerRate}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Answered Calls */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Llamadas Contestadas ({campaignReport.summary.answered})
                </Typography>
                {campaignReport.contacts.answered.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Teléfono</TableCell>
                        <TableCell>Nombre</TableCell>
                        <TableCell>Duración</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell align="center">Grabación</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {campaignReport.contacts.answered.map((contact) => (
                        <TableRow key={contact.id}>
                          <TableCell>{contact.phoneNumber}</TableCell>
                          <TableCell>{contact.name || "-"}</TableCell>
                          <TableCell>{contact.duration ? `${contact.duration}s` : "-"}</TableCell>
                          <TableCell>
                            {contact.lastAttemptAt
                              ? new Date(contact.lastAttemptAt).toLocaleString()
                              : "-"}
                          </TableCell>
                          <TableCell align="center">
                            {contact.recordingUrl ? (
                              <Tooltip title="Descargar Grabación">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleDownloadRecording(contact.id)}
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="textSecondary">
                                No disponible
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No hay llamadas contestadas
                  </Typography>
                )}
              </Grid>

              {/* Not Answered Calls */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Llamadas No Contestadas ({campaignReport.summary.notAnswered})
                </Typography>
                {campaignReport.contacts.notAnswered.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Teléfono</TableCell>
                        <TableCell>Nombre</TableCell>
                        <TableCell>Intentos</TableCell>
                        <TableCell>Error</TableCell>
                        <TableCell>Fecha</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {campaignReport.contacts.notAnswered.map((contact) => (
                        <TableRow key={contact.id}>
                          <TableCell>{contact.phoneNumber}</TableCell>
                          <TableCell>{contact.name || "-"}</TableCell>
                          <TableCell>{contact.attempts}</TableCell>
                          <TableCell>
                            <Typography variant="caption" color="error">
                              {contact.errorMessage || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {contact.lastAttemptAt
                              ? new Date(contact.lastAttemptAt).toLocaleString()
                              : "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No hay llamadas sin contestar
                  </Typography>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default VapiCalls;
